import { RefObject, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';

/**
 * Champagne-firework style coin burst. Coins erupt from the bounding-rect
 * center of `originRef`, arc upward and outward, then fall under "gravity"
 * and drift across the page.
 *
 * Implementation notes:
 *  - One shared CSS keyframe with three stops (start / apex / land) and
 *    per-stop timing functions: ease-out for the rise, ease-in for the fall.
 *    This is enough to fake parabolic motion without per-coin keyframes.
 *  - Per-coin variation comes from CSS custom properties (apex/land offsets,
 *    rotation, size, duration, delay) injected via inline `style`.
 *  - Renders into `document.body` so coins can fall over the entire viewport
 *    regardless of the parent component's stacking context.
 */

const COIN_COUNT = 24;
const APEX_PCT = 22;          // when in the timeline the coin reaches its peak
const BASE_DURATION_MS = 900;

const arc = keyframes`
    0% {
        transform: translate(-50%, -50%);
        opacity: 0;
        animation-timing-function: cubic-bezier(0, 0.55, 0.45, 1); /* ease-out: rise */
    }
    6% { opacity: 1; }
    ${APEX_PCT}% {
        transform: translate(calc(-50% + var(--xp)), calc(-50% + var(--yp))) rotate(var(--rotp));
        opacity: 1;
        animation-timing-function: cubic-bezier(0.7, 0, 0.95, 0.4); /* steeper ease-in: gravity-accelerated fall */
    }
    85% { opacity: 1; }
    100% {
        transform: translate(calc(-50% + var(--x1)), calc(-50% + var(--y1))) rotate(var(--rot1));
        opacity: 0;
    }
`;

interface Coin {
    xp: number, yp: number, // apex offset from origin (px)
    x1: number, y1: number, // landing offset from origin (px)
    rotp: number, rot1: number,
    duration: number,
    delay: number,
    size: number,
}

function generateCoins(): Coin[] {
    const out: Coin[] = [];
    for (let i = 0; i < COIN_COUNT; i++) {
        // Mostly upward angles: -160°..-20° from +x axis (i.e. above horizontal).
        const angleDeg = -160 + Math.random() * 140;
        const angle = (angleDeg * Math.PI) / 180;
        const launchSpeed = 180 + Math.random() * 160; // px-ish "velocity"

        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle); // negative (up = -y in screen coords)

        // Apex: offset along the launch vector.
        const apexDist = launchSpeed * 0.5;
        const xp = cosA * apexDist;
        const yp = sinA * apexDist;

        // Landing: horizontal carries through; vertical falls well past the
        // viewport so coins exit off-screen instead of stopping mid-air.
        // The fall component dominates so coins clearly accelerate downward.
        const fallDist = (typeof window !== 'undefined' ? window.innerHeight : 800) + 200;
        const x1 = cosA * launchSpeed * 1.1;
        const y1 = -sinA * launchSpeed * 0.4 + fallDist;

        out.push({
            xp, yp, x1, y1,
            rotp: (Math.random() - 0.5) * 360,
            rot1: (Math.random() - 0.5) * 1100,
            duration: BASE_DURATION_MS + Math.random() * 250,
            delay: Math.random() * 60,
            size: 18 + Math.random() * 12,
        });
    }
    return out;
}

interface Props {
    /** Bumping this value triggers a fresh burst. */
    triggerKey: number,
    /** Element to anchor the burst's origin (e.g. the user avatar). */
    originRef: RefObject<HTMLElement | null>,
}

export default function CoinBurst({ triggerKey, originRef }: Props) {
    const [origin, setOrigin] = useState<{ x: number, y: number } | null>(null);
    const [coins, setCoins] = useState<Coin[]>([]);

    const reduced = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    useEffect(() => {
        if (triggerKey === 0 || reduced) return;
        const rect = originRef.current?.getBoundingClientRect();
        if (!rect) return;
        setOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
        const next = generateCoins();
        setCoins(next);
        const maxLife = next.reduce((m, c) => Math.max(m, c.duration + c.delay), 0) + 100;
        const t = window.setTimeout(() => {
            setOrigin(null);
            setCoins([]);
        }, maxLife);
        return () => window.clearTimeout(t);
    }, [triggerKey, reduced, originRef]);

    if (!origin || coins.length === 0 || typeof document === 'undefined') return null;

    return createPortal(
        <Box
            aria-hidden
            sx={{
                position: 'fixed',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: (theme) => theme.zIndex.tooltip + 1,
            }}
        >
            {coins.map((c, i) => (
                <Box
                    key={`${triggerKey}-${i}`}
                    component="span"
                    style={{
                        left: origin.x,
                        top: origin.y,
                        fontSize: c.size,
                        animationDuration: `${c.duration}ms`,
                        animationDelay: `${c.delay}ms`,
                        ['--xp' as never]: `${c.xp}px`,
                        ['--yp' as never]: `${c.yp}px`,
                        ['--x1' as never]: `${c.x1}px`,
                        ['--y1' as never]: `${c.y1}px`,
                        ['--rotp' as never]: `${c.rotp}deg`,
                        ['--rot1' as never]: `${c.rot1}deg`,
                    }}
                    sx={{
                        position: 'absolute',
                        lineHeight: 1,
                        animationName: `${arc}`,
                        animationFillMode: 'forwards',
                        willChange: 'transform, opacity',
                    }}
                >
                    🪙
                </Box>
            ))}
        </Box>,
        document.body,
    );
}
